import socketService from "@/socketService"

export function mergeBookingFields(current, fresh) {
  const result = { ...current }
  for (const field of ["amount", "signupAmount", "signups", "signoffs", "lsSignups", "lsSignoffs"]) {
    if (fresh[field] !== undefined) result[field] = fresh[field]
  }
  for (const field of ["signedUp", "lsSignedUp", "onQueue", "visited"]) result[field] = Boolean(fresh[field])
  result.queuePosition = fresh.queuePosition ?? null
  return result
}

// Coalesce booking bursts, never poll, and permit only one live refresh at a time.
export function createCourseLive({ studioId, relevant, busy, refresh }) {
  let stopped = false
  let generation = 0
  let timer = null
  let running = false
  let pending = []

  const schedule = () => {
    clearTimeout(timer)
    if (!stopped && pending.length && !document.hidden) timer = setTimeout(flush, 750)
  }
  const enqueue = (change = null) => {
    if (stopped || (change && (String(change.studioId) !== String(studioId()) || !relevant(change)))) return
    // null requests a catch-up after reconnect or returning to the tab.
    pending = !change || pending.includes(null) || pending.length >= 50 ? [null] : [...pending, change]
    schedule()
  }
  const flush = async () => {
    if (stopped || running || busy() || document.hidden || !pending.length) return
    const changes = pending
    pending = []
    running = true
    try { await refresh(changes) }
    catch (_) { /* Retain current data; the next event or reconnect can retry. */ }
    finally { running = false; schedule() }
  }
  const subscribe = async () => {
    const version = ++generation
    pending = []
    clearTimeout(timer)
    if (stopped || !studioId() || !socketService.socket?.connected) return
    const id = String(studioId())
    try {
      await socketService.emitAck("gf:subscribe", { studioId: id })
      if (!stopped && version === generation && id === String(studioId())) enqueue()
    } catch (_) { /* The normal HTTP view works without live support. */ }
  }
  const visible = () => { if (!document.hidden) enqueue() }
  socketService.on("gf:bookings-changed", enqueue)
  socketService.on("connect", subscribe)
  document.addEventListener("visibilitychange", visible)
  subscribe()

  return {
    subscribe,
    flush: schedule,
    stop() {
      stopped = true
      generation++
      pending = []
      clearTimeout(timer)
      socketService.off("gf:bookings-changed", enqueue)
      socketService.off("connect", subscribe)
      socketService.emit("gf:unsubscribe")
      document.removeEventListener("visibilitychange", visible)
    },
  }
}
